import Graph from './Graph';
import { getEvenlySpacedPoints } from './graphUtils';
import { clearNonBlockingMessageWithMessage, showNonBlockingMessage } from '../ui/uiUtils';
import { splitTextIntoWords } from '../apiRequests';
import * as hints from '../tutorials/hints';
import * as tutorialEvents from '../tutorials/tutorialEvents';
import { startInspireTutorial } from '../tutorials/inspireTutorial';
import { isTutorialActive } from '../tutorials/tutorial';
import { getPointsSortedByClosestToCenter } from './geometryUtils';
import { closeAiContainers, updateInspireProgress } from '../ui/toolsUI';
import * as templateTutorial from '../tutorials/templateTutorial';
import {
	AvailableColour,
	NCGraphData,
	NCNode,
	BoardUser,
	GraphMode,
	UserSelection,
	NCGroup,
	TopicAnalysis,
} from '../../../../src/commonTypes';
import { getLinksInvolvingNode } from './linkUtils';
import { isLongNodeLabel } from '../../../../src/graphNodeShape';

class InspireGraph extends Graph {
	constructor(
		data: NCGraphData,
		boardUsers: Array<BoardUser>,
		userSelections: Array<UserSelection>,
		colourClass: AvailableColour,
		colourHex: string,
		automation: boolean
	) {
		super(data, boardUsers, userSelections, colourClass, colourHex, true, automation);

		this.appendMenuElements();

		updateInspireProgress(this);
	}

	getMenuOptions() {
		const activeNode = this.activeNode;
		const isLocalPdfNode = Boolean(activeNode && this.isLocalPdfGraphNode(activeNode));
		const isLongNode = Boolean(
			isLocalPdfNode || activeNode?.style === 'rect' || isLongNodeLabel(activeNode?.label)
		);

		if (activeNode?.isVideo) {
			return [
				{ label: 'Search', enabled: true, longText: true, shortText: true },
				{ label: 'Connect', enabled: true, longText: true, shortText: true },
				{ label: 'Like', enabled: true, longText: true, shortText: true },
			];
		}

		return [
			{
				label: 'Generate',
				enabled: !activeNode?.src,
				requiresAi: true,
				longText: true,
				shortText: true,
				radial: true,
			},
			{
				label: 'Empathise',
				enabled: !activeNode?.src,
				requiresAi: true,
				shortText: true,
				radial: true,
			},
			{ label: 'Spark', enabled: !activeNode?.src, requiresAi: true, shortText: true, radial: true },
			{ label: 'Like', enabled: true, longText: true, shortText: true, radial: true },
			{
				label: 'Merge',
				enabled: this.isActiveNodeOwnedByCurrentUser() && !activeNode?.src,
				longText: true,
				radial: true,
			},
			{ label: 'Connect', enabled: true, longText: true, shortText: true, radial: true },
			{ label: 'Search', enabled: true, longText: true, shortText: true, radial: true },
			{
				label: 'Expand',
				enabled: isLongNode,
				longText: true,
				radial: true,
			},
		];
	}

	get nodeRadius() {
		return 64;
	}

	/**
	 * Merge two nodes (or unmerged a previously merged node)
	 */
	merge(node: NCNode) {
		closeAiContainers();

		if (node.merged) {
			this.splitNodes(node, true);
		} else {
			this.startSelectingNodes('mergeFirstNode', [node]);

			showNonBlockingMessage(
				`This will merge "${
					node.label || ''
				}" to another node, combining into a phrase.<br> <strong>Select another node to merge with<strong>`,
				[
					{
						label: 'Cancel',
						onClick: () => {
							this.stopSelectingNodes();
						},
					},
				]
			);

			tutorialEvents.clickedMerge(node);
		}
	}

	/**
	 * Linker AI tool
	 */
	linker(node: NCNode) {
		if (!this.checkNodeReadyForAI(node, 'Link')) {
			return;
		}

		closeAiContainers();

		this.startSelectingNodes('linkingNodes', [node]);

		showNonBlockingMessage('Select one or more other nodes to find a linking concept', [
			{
				label: 'Cancel',
				onClick: () => {
					this.stopSelectingNodes();
				},
			},
		]);

		tutorialEvents.clickedLink(node);
		this.setToolUsed('link');
	}

	async toggleAi() {
		super.toggleAi();

		if (this.aiStatus) {
			if (await this.splitNodes()) {
				setTimeout(() => {
					this.zoomToFit();
				}, 250);
			}

			if (!this.templateId) {
				tutorialEvents.aiEnabled();
			} else if (this.templateId === 'branding') {
				tutorialEvents.aiEnabled();
			}
		}
	}

	async autoSpark(options: { deferTutorial?: boolean } = {}): Promise<void> {
		if (!isTutorialActive('inspire')) {
			if (!this.getHintsShown()) {
				const demoNodes = getPointsSortedByClosestToCenter(
					this.nodes.filter((n) => n.style !== 'rect')
				).slice(0, 3);

				if (!demoNodes.length) {
					return;
				}

				tutorialEvents.autoSparkStarted();
				showNonBlockingMessage('Adding suggested directions to your board…');
				try {
					for (const demoNode of demoNodes) {
						// Keep auto-spark an invisible seeding step: suggestions are added
						// directly as dotted nodes, without opening or populating Spark UI.
						try {
							// eslint-disable-next-line no-await-in-loop
							await this.addBootstrapSparkSuggestion(demoNode);
						} catch (error) {
							// One unavailable Spark request must not prevent the initial
							// welcome or the remaining seed suggestions.
							console.warn('Bootstrap Spark suggestion failed', error);
						}
					}
					if (!options.deferTutorial) {
						tutorialEvents.afterAutoSpark(demoNodes);
					}
				} finally {
					clearNonBlockingMessageWithMessage('Adding suggested directions');
					if (options.deferTutorial) {
						tutorialEvents.bootstrapAutoSparkFinished();
					}
					tutorialEvents.autoSparkFinished();
				}
			}
		}
	}

	async addWordsToBoard(wordsString: string, centralNodeString?: string) {
		const words = await splitTextIntoWords(this.boardId, wordsString, 'keywords');
		if (centralNodeString) {
			const cluster = {
				group: centralNodeString,
				values: words,
			};
			this.spawnCluster(cluster, { x: 0, y: 0, colourClass: this.userColourClass }, true);
		} else {
			const points = getEvenlySpacedPoints(words.length);
			words
				.filter((w) => w)
				.forEach((w, index) => {
					const { x, y } = points[index];
					this.spawnNewNode(
						{
							label: w,
							x,
							y,
						},
						null,
						true
					);
				});
		}
	}

	addPhrasesToBoard(centralTitle: string, phrases: TopicAnalysis[]) {
		const phraseLabels = phrases.map((phrase) => phrase.key).filter(Boolean);
		if (!phraseLabels.length) {
			return;
		}

		this.spawnCluster(
			{
				group: centralTitle,
				values: phraseLabels,
			},
			{
				x: 0,
				y: 0,
				colourClass: this.userColourClass,
				radius: 100 + phraseLabels.length * 10,
			},
			true,
			phrases
		);
	}

	async addPdfContextToBoard(centralNodeString: string) {
		const cluster = {
			group: centralNodeString,
			values: [],
		};
		this.spawnCluster(cluster, { x: 0, y: 0, colourClass: this.userColourClass }, true);
	}

	updateGraphWithNewData(graphData: NCGraphData) {
		super.updateGraphWithNewData(graphData);

		updateInspireProgress(this);
	}

	areNodesColoured() {
		return this.isCollaborative;
	}

	areLinksColoured() {
		return false;
	}

	isEditable() {
		return true;
		// return !this.activeNode || this.isActiveNodeOwnedByCurrentUser();
	}

	get graphMode(): GraphMode {
		return 'inspire';
	}

	startTutorial() {
		super.startTutorial();
		if (this.templateId) {
			templateTutorial.start(this.templateId, this, true);
		} else {
			startInspireTutorial();
		}
	}

	startHints() {
		super.startHints();
		if (this.nodes.length > 0) {
			this.setHintsShown();
		} else {
			// Empty boards still need their input, project-vision and seeding flow. The user's
			// optional tutorial preferences only apply after that essential setup is complete.
			hints.beginInspireHint();
		}
	}

	setHintsShown() {
		super.setHintsShown();
		this.currentUser.settings.shown_inspire = true;
		this.saveUser();
	}

	getHintsShown(): boolean {
		return this.currentUser.settings.shown_inspire || false;
	}

	simulateInteractions() {
		// We will add 50 nodes to the board automatically simulating the user's interactions
		// This is used for testing collaboration between users by running from multiple browsers
		// The nodes are added in a sequence to simulate the user's interactions
		// Tip: test with 2 users, using low latency throttling in the network tab to see how robust the collaboration is
		const words = [
			'one',
			'two',
			'three',
			'four',
			'five',
			'six',
			'seven',
			'eight',
			'nine',
			'ten',
			'eleven',
			'twelve',
			'thirteen',
			'fourteen',
			'fifteen',
			'sixteen',
			'seventeen',
			'eighteen',
			'nineteen',
			'twenty',
			'twenty-one',
			'twenty-two',
			'twenty-three',
			'twenty-four',
			'twenty-five',
			'twenty-six',
			'twenty-seven',
			'twenty-eight',
			'twenty-nine',
			'thirty',
			'thirty-one',
			'thirty-two',
			'thirty-three',
			'thirty-four',
			'thirty-five',
			'thirty-six',
			'thirty-seven',
			'thirty-eight',
			'thirty-nine',
			'forty',
			'forty-one',
			'forty-two',
			'forty-three',
			'forty-four',
			'forty-five',
			'forty-six',
			'forty-seven',
			'forty-eight',
			'forty-nine',
			'fifty',
			'fifty-one',
			'fifty-two',
			'fifty-three',
			'fifty-four',
			'fifty-five',
			'fifty-six',
			'fifty-seven',
			'fifty-eight',
			'fifty-nine',
			'sixty',
			'sixty-one',
			'sixty-two',
			'sixty-three',
			'sixty-four',
			'sixty-five',
			'sixty-six',
			'sixty-seven',
			'sixty-eight',
			'sixty-nine',
			'seventy',
			'seventy-one',
			'seventy-two',
			'seventy-three',
			'seventy-four',
			'seventy-five',
			'seventy-six',
			'seventy-seven',
			'seventy-eight',
			'seventy-nine',
			'eighty',
			'eighty-one',
			'eighty-two',
			'eighty-three',
			'eighty-four',
			'eighty-five',
			'eighty-six',
			'eighty-seven',
			'eighty-eight',
			'eighty-nine',
			'ninety',
			'ninety-one',
			'ninety-two',
			'ninety-three',
			'ninety-four',
			'ninety-five',
			'ninety-six',
			'ninety-seven',
			'ninety-eight',
			'ninety-nine',
			'one hundred',
		];

		const delay = 500;
		let i = 0;
		const columnSize = 10;
		let lastNode: NCNode | null = null;
		const addedNodes: Array<NCNode> = [];
		let hasAddedNodes = false;
		let group: NCGroup | null = null;
		const interval = setInterval(() => {
			if (!hasAddedNodes && i < words.length) {
				lastNode = this.spawnNewNode(
					{
						label: words[i],
						x: (this.currentUserIndex - 1) * 1400 + 200 * Math.floor(i / columnSize),
						y: (i % columnSize) * 200,
					},
					lastNode && i % columnSize !== 0 ? [lastNode] : null,
					true
				);
				addedNodes.push(lastNode);
				i++;
			} else {
				hasAddedNodes = true;
				if (lastNode) {
					group = this.createGroup([addedNodes[0], addedNodes[1]]);
					lastNode = null;
					i = 2;
				} else {
					if (i < columnSize && group) {
						group = this.addNodesToGroup(group, [addedNodes[i]]);
						i++;
					} else if (i < columnSize * 2) {
						this.connectNodes(addedNodes[i], addedNodes[i + columnSize]);
						i++;
					} else if (i < columnSize * 3) {
						if (group!.nodes.length > 5) {
							group = this.removeNodesFromGroup(group!, [group!.nodes[0]]);
						}
						this.updateNodeLabel(addedNodes[i], `${addedNodes[i].label}-updated`);
						i++;
					} else if (i < columnSize * 4) {
						this.delete(addedNodes[i]);
						i++;
					} else if (i < columnSize * 5) {
						getLinksInvolvingNode(addedNodes[i], this.links).forEach((l) => {
							this.removeLink(l);
						});
						i++;
					} else {
						this.removeGroup(group!, true);
						this.updateActive(addedNodes.at(-1)!);
						clearInterval(interval);
					}
				}
			}
		}, delay);
	}
}

export default InspireGraph;
